import { ErrorMessage, Field } from 'formik';
import { useState } from 'react';

type PropTypes = {
	name: string;
	label: string;
	type: 'email' | 'password' | 'text' | 'tel';
	placeholder?: string;
	error?: boolean;
	touched?: boolean;
};

const EyeIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
		<path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
		<path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
	</svg>
);

const EyeSlashIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
		<path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
	</svg>
);

export default function AuthInput({
	name,
	label,
	type,
	placeholder,
	error,
	touched,
	...props
}: PropTypes) {
	const [showPassword, setShowPassword] = useState(false);
	const isPasswordField = type === 'password';
	const inputType = isPasswordField && showPassword ? 'text' : type;

	return (
		<div className="flex flex-col relative font-mono w-full min-h-14 autofill:font-mono my-4">
			<label
				className="text-xs font-light absolute bg-white text-gray-400 top-[-8px] left-[16px] px-1 rounded-full"
				htmlFor={name}
			>
				{label}
			</label>
			<div className="relative">
				<Field
					className={`border-[1px] border-gray-400 rounded-md px-5 py-2 text-sm focus:border-[#0F9015] outline-none focus:shadow-[#0F9015]/30 focus:shadow-outline data-[error=true]:border-red-500 w-full ${isPasswordField ? 'pr-12' : ''}`}
					type={inputType}
					id={name}
					name={name}
					maxLength={(type === 'tel' && 6) || 'false'}
					placeholder={placeholder || ''}
					data-error={error && touched}
					{...props}
				/>
				{isPasswordField && (
					<button
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none bg-transparent border-none p-0"
						tabIndex={-1}
						aria-label={showPassword ? 'Hide password' : 'Show password'}
					>
						{showPassword ? <EyeSlashIcon /> : <EyeIcon />}
					</button>
				)}
			</div>
			<div className="w-full h-3" data-testid={`form-input-error-${name}`}>
				<ErrorMessage
					className="text-red-500 text-xs w-full"
					name={name}
					component="p"
				/>
			</div>
		</div>
	);
}
